import React, { FC } from 'react'

import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'

import Typography from 'components/common/atoms/Typography'
import SVGIcon from 'components/common/atoms/SVGIcon'

type FAQProps = {
  question: string
  answer: React.ReactNode
  active: boolean
  onClick: () => void
}

const FAQStyle = (theme: ThemeType) => css`
  .faq {
    &__wrapper {
      margin-bottom: 16px;
      border-radius: 10px;
      background-color: ${theme.neutral.white};
      box-shadow: 0 2px 12px 0 rgba(26, 52, 79, 0.1);
      &:hover {
        .faq__question {
          box-shadow: 0 2px 8px 0 rgba(26, 52, 79, 0.4);
          h3 {
            color: ${theme.campus.aqua30};
          }
        }
      }
      &.active {
        .faq__question {
          color: ${theme.gray.gray70};
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        .icon-wrapper {
          transform: rotate(360deg);
        }
      }
    }
    &__question {
      padding: 24px;
      border-radius: 10px;
      cursor: pointer;
      color: ${theme.gray.gray50};
      display: flex;
      justify-content: space-between;
      align-items: center;
      .icon-wrapper {
        transform: rotate(180deg);
        transition: transform 0.25s ease;
      }
    }
    &__answer {
      padding: 24px;
      box-shadow: 0 2px 12px 0 rgba(26, 52, 79, 0.1);
      background-color: ${theme.gray.gray10};
      border-bottom-right-radius: 10px;
      border-bottom-left-radius: 10px;
      box-shadow: inset 0 3px 5px -4px rgba(26, 52, 79, 0.4);
      > ul {
        margin-left: 24px;
      }
      p,
      li {
        margin-bottom: 0;
        font-size: 14px;
        line-height: 21px;
      }
    }
    &__answer-wrapper {
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: all 0.3s ease;
      &.active {
        max-height: 500px;
        opacity: 1;
      }
    }
    @media screen and (min-width: 768px) {
      &__answer {
        p,
        li {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }
  }
`

const FAQ: FC<FAQProps> = ({ question, answer, active, onClick }) => {
  const theme: ThemeType = useTheme()
  return (
    <div className="faq" onClick={onClick} onKeyDown={onClick} css={FAQStyle(theme)}>
      <div className={`faq__wrapper${active ? ' active' : ''}`}>
        <div className="faq__question">
          <Typography variant="paragraph" level="1" tagName="h3" fontWeight="bold">
            {question}
          </Typography>
          <SVGIcon name="up" size="md" color={theme.gray.gray50} />
        </div>
        <div className={`faq__answer-wrapper${active ? ' active' : ''}`}>{answer}</div>
      </div>
    </div>
  )
}

export default FAQ
